import React, { useState, useEffect, useRef, useCallback } from "react";
import ReactDOM from "react-dom";
import { useData } from "../context/DataContext";
import { useAuth } from "../context/AuthContext";
import { getPriorityBadge } from "../utils/helpers";

// Common words for auto-suggestion (sorted alphabetically for fast lookup)
const SUGGESTION_WORDS = [
  "about", "above", "accepted", "according", "account", "acknowledged",
  "action", "added", "additional", "address", "after", "again", "agree",
  "already", "also", "amount", "analysis", "another", "answer", "anyone",
  "anything", "application", "approved", "assigned", "attached", "attachment",
  "available", "balance", "before", "below", "between", "budget", "business",
  "called", "cancelled", "change", "changed", "checked", "clarify", "client",
  "close", "closed", "comment", "company", "complete", "completed", "concerned",
  "confirm", "confirmed", "contact", "continue", "correct", "could", "create",
  "created", "current", "customer", "daily", "data", "deadline", "delay",
  "delayed", "delete", "delivered", "delivery", "department", "details",
  "discuss", "discussed", "document", "doing", "done", "during", "earlier",
  "email", "employee", "escalate", "escalated", "estimate", "everyone",
  "excellent", "expected", "explain", "feedback", "figure", "file", "final",
  "finance", "finished", "fixed", "follow", "following", "forward", "forwarded",
  "found", "from", "good", "great", "handle", "handled", "hello", "help",
  "here", "however", "immediate", "immediately", "important", "improve",
  "include", "included", "inform", "information", "initial", "internal",
  "invoice", "issue", "just", "keep", "kindly", "know", "later", "latest",
  "leave", "looking", "maintain", "manager", "master", "meeting", "mention",
  "mentioned", "message", "might", "month", "monthly", "morning", "move",
  "much", "necessary", "need", "needed", "next", "noted", "nothing", "notify",
  "number", "okay", "once", "only", "open", "opened", "order", "other",
  "overdue", "paid", "payment", "pending", "please", "point", "postponed",
  "prepare", "prepared", "previous", "priority", "process", "processed",
  "progress", "project", "provide", "provided", "purchase", "query",
  "question", "ready", "reason", "received", "record", "reference", "regards",
  "rejected", "related", "remaining", "remove", "removed", "reply", "report",
  "request", "requested", "required", "resolved", "response", "result",
  "review", "reviewed", "revised", "right", "running", "scheduled", "send",
  "sent", "share", "shared", "should", "showing", "signed", "since", "sorry",
  "status", "still", "submitted", "suggest", "suggestion", "supervisor",
  "support", "sure", "system", "taken", "task", "team", "test", "testing",
  "thank", "thanks", "that", "their", "there", "these", "thing", "think",
  "this", "through", "time", "today", "together", "tomorrow", "total",
  "tracking", "transfer", "update", "updated", "upload", "uploaded", "urgent",
  "verified", "waiting", "want", "week", "weekly", "welcome", "what", "when",
  "where", "which", "while", "will", "with", "work", "working", "would",
  "write", "wrong", "year", "yesterday",
];

export default function CommentPanel({ taskId, taskName, priority, onClose }) {
  const { getComments, addComment, getAttachments, uploadFile, markCommentsRead } = useData();
  const { user } = useAuth();
  const [comments, setComments] = useState([]);
  const [text, setText] = useState("");
  const [loading, setLoading] = useState(true);
  const [attachments, setAttachments] = useState([]);
  const [sending, setSending] = useState(false);
  const [pendingFiles, setPendingFiles] = useState([]); // files staged for send
  const [uploading, setUploading] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [selectedSuggIdx, setSelectedSuggIdx] = useState(0);
  const bottomRef = useRef(null);
  const textareaRef = useRef(null);
  const fileInputRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const [comments, attachments] = await Promise.all([
        getComments(taskId),
        getAttachments(taskId),
      ]);
      setComments(comments);
      setAttachments(attachments);
      markCommentsRead(taskId, comments.length);
    } catch(e) { console.error(e); }
    finally { setLoading(false); }
  }, [taskId, getComments, getAttachments]);

  useEffect(() => { load(); }, [load]);

  // Scroll to bottom inside panel only
  useEffect(() => {
    if (bottomRef.current) {
      const container = bottomRef.current.parentElement;
      container.scrollTop = container.scrollHeight;
    }
  }, [comments]);

  // Auto-suggestion logic — "starts with" first, then "contains", up to 10 results
  const updateSuggestions = useCallback((value) => {
    const words = value.split(/\s/);
    const lastWord = (words[words.length - 1] || "").toLowerCase();
    if (lastWord.length < 2) { setSuggestions([]); setShowSuggestions(false); return; }
    const startsWith = [];
    const contains = [];
    for (const w of SUGGESTION_WORDS) {
      if (w === lastWord) continue;
      if (w.startsWith(lastWord)) startsWith.push(w);
      else if (w.includes(lastWord)) contains.push(w);
    }
    const matches = [...startsWith, ...contains].slice(0, 10);
    setSuggestions(matches);
    setShowSuggestions(matches.length > 0);
    setSelectedSuggIdx(0);
  }, []);

  const applySuggestion = (word) => {
    const words = text.split(/\s/);
    words[words.length - 1] = word;
    const newText = words.join(" ") + " ";
    setText(newText);
    setShowSuggestions(false);
    setSuggestions([]);
    textareaRef.current?.focus();
  };

  // Handle paste for files (images, documents, etc.)
  const handlePaste = useCallback((e) => {
    const items = e.clipboardData?.items;
    if (!items) return;
    let fileAdded = false;
    for (let i = 0; i < items.length; i++) {
      if (items[i].kind === "file") {
        e.preventDefault();
        const file = items[i].getAsFile();
        if (file) {
          // If image with no real name, give it a timestamp name
          const name = (file.name && file.name !== "image.png" && file.name !== "blob")
            ? file.name
            : `pasted-${file.type.startsWith("image/") ? "image" : "file"}-${Date.now()}.${file.type.split("/")[1] || "png"}`;
          const named = new File([file], name, { type: file.type });
          setPendingFiles(prev => [...prev, named]);
          fileAdded = true;
        }
      }
    }
    // If no actual file was found in clipboard but text looks like a filename, let it pass as normal text
    if (fileAdded) return;
  }, []);

  // Handle drag-and-drop files onto the chat area
  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    e.currentTarget.style.borderColor = "var(--primary)";
    e.currentTarget.style.background = "var(--primary-muted)";
  }, []);
  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    e.currentTarget.style.borderColor = "var(--border-default)";
    e.currentTarget.style.background = "var(--surface-1)";
  }, []);
  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    e.currentTarget.style.borderColor = "var(--border-default)";
    e.currentTarget.style.background = "var(--surface-1)";
    const files = Array.from(e.dataTransfer?.files || []);
    if (files.length > 0) {
      setPendingFiles(prev => [...prev, ...files]);
    }
  }, []);

  // Handle file selection
  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length) setPendingFiles(prev => [...prev, ...files]);
    e.target.value = null;
  };

  const removePendingFile = (idx) => {
    setPendingFiles(prev => prev.filter((_, i) => i !== idx));
  };

  const send = async () => {
    if ((!text.trim() && pendingFiles.length === 0) || sending) return;
    setSending(true);
    setShowSuggestions(false);
    try {
      // Upload any pending files first
      if (pendingFiles.length > 0) {
        setUploading(true);
        for (const file of pendingFiles) {
          await uploadFile(file, String(taskId));
        }
        setUploading(false);
        setPendingFiles([]);
      }

      // Send comment text (or auto-text for file-only messages)
      const msgText = text.trim() || (pendingFiles.length > 0 ? `📎 Attached ${pendingFiles.length} file(s)` : "");
      if (msgText) {
        const res = await addComment(taskId, msgText);
        setComments(p => {
          const next = [...p, res.comment];
          markCommentsRead(taskId, next.length);
          return next;
        });
      }
      setText("");
      // Reload attachments to show new uploads
      const freshAttachments = await getAttachments(taskId);
      setAttachments(freshAttachments);
    } catch(e) { alert("Failed: " + e.message); }
    finally { setSending(false); setUploading(false); }
  };

  const handleKey = (e) => {
    if (showSuggestions && suggestions.length > 0) {
      if (e.key === "ArrowDown") { e.preventDefault(); setSelectedSuggIdx(i => Math.min(i + 1, suggestions.length - 1)); return; }
      if (e.key === "ArrowUp") { e.preventDefault(); setSelectedSuggIdx(i => Math.max(i - 1, 0)); return; }
      if (e.key === "Tab" || (e.key === "Enter" && !e.shiftKey)) {
        e.preventDefault();
        applySuggestion(suggestions[selectedSuggIdx]);
        return;
      }
      if (e.key === "Escape") { setShowSuggestions(false); return; }
    }
    if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); }
  };

  const formatTime = (ts) => {
    const d = new Date(ts);
    const diffMins = Math.floor((Date.now() - d) / 60000);
    if (diffMins < 1) return "just now";
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffMins < 1440) return `${Math.floor(diffMins/60)}h ago`;
    return d.toLocaleDateString();
  };

  const roleColor = (role) => role === "admin" ? "var(--primary)" : role === "supervisor" ? "var(--success)" : "var(--purple)";
  const roleBg    = (role) => role === "admin" ? "var(--primary-muted)" : role === "supervisor" ? "var(--success-muted)" : "var(--purple-muted)";

  const isImage = (name) => /\.(png|jpg|jpeg|gif|webp|svg|bmp)$/i.test(name || "");

  const modalContent = (
    <>
      <style>{`
        .comment-panel-input-area {
          padding: 10px 14px max(14px, env(safe-area-inset-bottom)) !important;
          flex-shrink: 0;
          border-top: 1px solid var(--border-subtle);
          background: var(--surface-1);
        }
        .sugg-item { padding: 6px 12px; cursor: pointer; font-size: 12px; font-family: inherit; }
        .sugg-item:hover, .sugg-item.active { background: var(--primary-muted); color: var(--primary); }
        .pending-file-chip {
          display: inline-flex; align-items: center; gap: 4px;
          padding: 3px 8px; border-radius: 8; font-size: 11px;
          background: var(--primary-muted); color: var(--primary);
          border: 1px solid var(--primary-border); max-width: 160px;
        }
        .pending-file-chip span { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
        .pending-file-chip button {
          border: none; background: none; color: var(--danger); cursor: pointer;
          font-size: 12px; padding: 0; line-height: 1; font-weight: 700;
        }
        .chat-img-preview { max-width: 220px; max-height: 180px; border-radius: 8px; margin-top: 6px; cursor: pointer; }
      `}</style>

      <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal" style={{
        height: "85vh", width: "100%", maxWidth: 500,
        background:"var(--surface-0)", boxShadow:"var(--shadow-xl)",
        borderRadius: "var(--radius-xl)", border: "1px solid var(--border-default)",
        display:"flex", flexDirection:"column", fontFamily:"inherit",
        overflow:"hidden", boxSizing:"border-box", margin: "auto"
      }}>
        {/* Header */}
        <div style={{
          padding:"18px 18px 14px", borderBottom:"1px solid var(--border-subtle)",
          background:"linear-gradient(135deg, var(--primary-dark), var(--primary))", color:"var(--surface-0)", flexShrink:0,
        }}>
          <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start" }}>
            <div style={{ flex:1, minWidth:0 }}>
              <div style={{ fontSize:10, fontWeight:700, opacity:0.8, letterSpacing:"0.08em", textTransform:"uppercase", marginBottom:4 }}>
                💬 Task Comments
              </div>
              <div style={{ fontSize:14, fontWeight:700, lineHeight:1.3, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap", display:"flex", alignItems:"center", gap:8 }}>
                {taskName}
                {(() => {
                  const pb = getPriorityBadge(priority);
                  return (
                    <span style={{
                      fontSize: 9, padding: "2px 6px", borderRadius: 10,
                      background: "rgba(255,255,255,0.2)", color: "white",
                      border: "1px solid rgba(255,255,255,0.3)", fontWeight: 600
                    }}>
                      {pb.label}
                    </span>
                  );
                })()}
              </div>
              <div style={{ fontSize:11, opacity:0.7, marginTop:3 }}>
                {comments.length} message{comments.length !== 1 ? "s" : ""}
                {attachments.length > 0 && ` · ${attachments.length} file${attachments.length !== 1 ? "s" : ""}`}
              </div>
            </div>
            <button onClick={onClose} aria-label="Close comments" style={{
              background:"rgba(255,255,255,0.15)", border:"1px solid rgba(255,255,255,0.2)", color:"white",
              width:32, height:32, borderRadius:8, cursor:"pointer", fontSize:18,
              display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0, marginLeft:10,
              transition:"var(--transition-fast)",
            }}>×</button>
          </div>
        </div>

        {/* Messages */}
        <div style={{ flex:"1 1 0", minHeight:0, overflowY:"auto", overflowX:"hidden", padding:"16px 14px", display:"flex", flexDirection:"column", gap:12, scrollBehavior:"smooth" }}>
          {attachments.length > 0 && (
          <div style={{ padding:"10px 14px", background:"var(--warning-muted)", borderBottom:"1px solid var(--border-subtle)", flexShrink:0, borderRadius:"var(--radius)" }}>
            <div style={{ fontSize:10, fontWeight:700, color:"var(--warning)", textTransform:"uppercase", letterSpacing:"0.06em", marginBottom:6 }}>📎 Attached Files</div>
            <div style={{ display:"flex", flexWrap:"wrap", gap:6 }}>
              {attachments.map((a, i) => (
                <div key={i} style={{
                  display:"flex", alignItems:"center", gap:5,
                  background:"var(--surface-1)", border:"1px solid var(--border-subtle)",
                  borderRadius:8, padding:"4px 10px", fontSize:12,
                }}>
                  <span>{isImage(a.fileName) ? "🖼️" : "📄"}</span>
                  <a href={`https://aquint-task-management.aged-wildflower-4467.workers.dev/api/download/${a.fileToken}`}
                    target="_blank" rel="noopener noreferrer"
                    style={{ color:"var(--primary)", maxWidth:160, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap", textDecoration:"none" }}>
                    {a.fileName} ↓
                  </a>
                  <span style={{ color:"var(--text-disabled)", fontSize:10 }}>({(a.fileSize/1024).toFixed(0)}KB)</span>
                </div>
              ))}
            </div>
          </div>
        )}
        {loading && (
            <div style={{ textAlign:"center", color:"var(--text-disabled)", fontSize:13, padding:40 }}>
              Loading comments...
            </div>
          )}
          {!loading && comments.length === 0 && (
            <div style={{ textAlign:"center", padding:"50px 20px" }}>
              <div style={{ fontSize:40, marginBottom:10 }}>💬</div>
              <div style={{ fontSize:13, color:"var(--text-disabled)", fontWeight:600 }}>No comments yet</div>
              <div style={{ fontSize:12, color:"var(--text-tertiary)", marginTop:4 }}>Be the first to comment on this task</div>
            </div>
          )}
          {comments.map((c) => {
            const isMe = c.authorId === user?.userId;
            // Check if comment text references an attachment
            const hasAttachmentRef = c.text?.includes("📎");
            return (
              <div key={c.id} style={{
                display:"flex", gap:9,
                flexDirection: isMe ? "row-reverse" : "row",
                alignItems:"flex-end",
              }}>
                <div style={{
                  width:32, height:32, borderRadius:"50%", flexShrink:0,
                  background: roleColor(c.authorRole),
                  display:"flex", alignItems:"center", justifyContent:"center",
                  fontSize:11, fontWeight:700, color:"white",
                }}>
                  {c.initials || c.authorName?.[0]?.toUpperCase() || "?"}
                </div>
                <div style={{ maxWidth:"72%", display:"flex", flexDirection:"column", gap:3, alignItems: isMe ? "flex-end" : "flex-start" }}>
                  <div style={{ display:"flex", alignItems:"center", gap:6, flexDirection: isMe ? "row-reverse" : "row" }}>
                    <span style={{ fontSize:11, fontWeight:700, color:"var(--text-primary)" }}>{isMe ? "You" : c.authorName}</span>
                    <span style={{
                      fontSize:9, fontWeight:700, padding:"1px 6px", borderRadius:10,
                      background: roleBg(c.authorRole), color: roleColor(c.authorRole),
                      textTransform:"uppercase", letterSpacing:"0.05em",
                    }}>{c.authorRole}</span>
                  </div>
                  <div style={{
                    padding:"10px 14px",
                    borderRadius: isMe ? "14px 4px 14px 14px" : "4px 14px 14px 14px",
                    background: isMe ? "linear-gradient(135deg, var(--primary-dark), var(--primary))" : "var(--surface-2)",
                    color: isMe ? "var(--surface-0)" : "var(--text-primary)",
                    fontSize:13, lineHeight:1.55,
                    border: isMe ? "none" : "1px solid var(--border-subtle)",
                    boxShadow:"var(--shadow-xs)",
                    whiteSpace:"pre-wrap", wordBreak:"break-word",
                  }}>
                    {c.text}
                  </div>
                  <span style={{ fontSize:10, color:"var(--text-disabled)" }}>{formatTime(c.timestamp)}</span>
                </div>
              </div>
            );
          })}
          <div ref={bottomRef} />
        </div>

        {/* Pending Files Preview */}
        {pendingFiles.length > 0 && (
          <div style={{ padding:"8px 14px", borderTop:"1px solid var(--border-subtle)", background:"var(--surface-1)", display:"flex", flexWrap:"wrap", gap:6, flexShrink:0 }}>
            {pendingFiles.map((f, idx) => (
              <div key={idx} className="pending-file-chip">
                <span>{isImage(f.name) ? "🖼️" : "📄"}</span>
                <span>{f.name}</span>
                <button onClick={() => removePendingFile(idx)} title="Remove">×</button>
              </div>
            ))}
          </div>
        )}

        {/* Input */}
        <div className="comment-panel-input-area">
          <div style={{ display:"flex", gap:8, alignItems:"flex-end" }}>
            <div style={{
              width:34, height:34, borderRadius:"50%", flexShrink:0,
              background: user?.role === "admin" ? "var(--primary)" : "var(--purple)",
              display:"flex", alignItems:"center", justifyContent:"center",
              fontSize:11, fontWeight:700, color:"var(--surface-0)", overflow:"hidden",
            }}>
              {user?.avatar
                ? <img src={user.avatar} alt="" style={{ width:"100%", height:"100%", objectFit:"cover" }} />
                : (user?.initials || user?.name?.[0]?.toUpperCase() || "?")}
            </div>
            <div style={{ flex:1, position:"relative" }}>
              <textarea
                ref={textareaRef}
                value={text}
                onChange={e => { setText(e.target.value); updateSuggestions(e.target.value); }}
                onKeyDown={handleKey}
                onPaste={handlePaste}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
                placeholder="Write a comment... (Enter to send)"
                rows={2}
                spellCheck={true}
                autoCorrect="on"
                autoCapitalize="sentences"
                lang="en"
                style={{
                  width:"100%", padding:"10px 80px 10px 13px",
                  border:"1.5px solid var(--border-default)", borderRadius:12,
                  fontSize:13, fontFamily:"inherit", outline:"none",
                  resize:"none", lineHeight:1.5, background:"var(--surface-1)",
                  color:"var(--text-primary)", boxSizing:"border-box", transition:"border-color 0.2s, background 0.2s",
                }}
                onFocus={e => e.target.style.borderColor="var(--primary)"}
                onBlur={e => { e.target.style.borderColor="var(--border-default)"; setTimeout(() => setShowSuggestions(false), 200); }}
              />
              {/* Suggestions dropdown */}
              {showSuggestions && suggestions.length > 0 && (
                <div style={{
                  position:"absolute", bottom:"100%", left:0, right:0, marginBottom:4,
                  background:"var(--surface-0)", border:"1px solid var(--border-default)",
                  borderRadius:8, boxShadow:"var(--shadow-lg)", overflow:"hidden auto", zIndex:10,
                  maxHeight:220,
                }}>
                  <div style={{ padding:"4px 8px", fontSize:9, fontWeight:700, color:"var(--text-disabled)", textTransform:"uppercase", letterSpacing:"0.06em", borderBottom:"1px solid var(--border-subtle)" }}>
                    Suggestions ({suggestions.length})
                  </div>
                  {suggestions.map((s, idx) => (
                    <div key={s}
                      className={`sugg-item${idx === selectedSuggIdx ? " active" : ""}`}
                      onMouseDown={(e) => { e.preventDefault(); applySuggestion(s); }}
                    >
                      {s}
                    </div>
                  ))}
                </div>
              )}
              {/* Attach + Send buttons */}
              <div style={{ position:"absolute", right:8, bottom:8, display:"flex", gap:4, alignItems:"center" }}>
                <input type="file" ref={fileInputRef} multiple style={{ display:"none" }} onChange={handleFileSelect} />
                <button onClick={() => fileInputRef.current?.click()} aria-label="Attach file" title="Attach file" style={{
                  width:28, height:28, borderRadius:7, border:"1px solid var(--border-default)",
                  background:"var(--surface-2)", color:"var(--text-tertiary)",
                  cursor:"pointer", display:"flex", alignItems:"center", justifyContent:"center",
                  fontSize:14, transition:"all 0.2s",
                }}>📎</button>
                <button onClick={send} disabled={(!text.trim() && pendingFiles.length === 0) || sending} aria-label="Send comment" style={{
                  width:28, height:28, borderRadius:7, border:"none",
                  background: (text.trim() || pendingFiles.length > 0) ? "var(--primary)" : "var(--surface-2)",
                  color: (text.trim() || pendingFiles.length > 0) ? "var(--surface-0)" : "var(--text-tertiary)",
                  cursor: (text.trim() || pendingFiles.length > 0) ? "pointer" : "default",
                  display:"flex", alignItems:"center", justifyContent:"center",
                  fontSize:15, fontWeight:700, transition:"all 0.2s",
                }}>
                  {sending ? (uploading ? "📤" : "⏳") : "↑"}
                </button>
              </div>
            </div>
          </div>
          <div style={{ fontSize:10, color:"var(--text-disabled)", marginTop:5, marginLeft:42 }}>
            Enter to send · Shift+Enter for new line · 📎 attach or drag & drop files
          </div>
        </div>
      </div>
    </div>
    </>
  );

  return ReactDOM.createPortal(modalContent, document.body);
}
